import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Paper,
  Link,
  Divider,
  Stack
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  LocalOffer as OfferIcon,
  Warning as WarningIcon,
  Link as LinkIcon,
  Schedule as ScheduleIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { 
  parseActivityContent, 
  formatActivityType, 
  formatPriority
} from '../utils/activityFormatter';
import { alpha, useTheme } from '@mui/material/styles';

const ActivityDisplay = ({ activity }) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();

  // Parse the activity content
  const parsedContent = parseActivityContent(activity.notes || activity.description);
  // Format metadata
  const activityType = formatActivityType(activity.activity_type);
  const priority = formatPriority(activity.priority);
  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Invalid date';
    }
  };

  // Render content in a more subtle, unified way


  const renderFormattedContent = () => {
    if (!activity.notes) return null;

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = activity.notes;

    const processedContent = tempDiv.innerHTML
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<p[^>]*>/gi, '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .trim();

    const sanitizeLine = (line) => line.replace(/^[-•\u2022]\s*/, '').trim();

    const lines = processedContent
      .split('\n')
      .map(sanitizeLine)
      .filter(Boolean);

    const blocks = [];
    let activeCallout = null;
    let lastStructuredBlock = null;
    const commitCallout = (type, title) => {
      const calloutBlock = { kind: 'callout', calloutType: type, title: title?.trim() || '', items: [] };
      blocks.push(calloutBlock);
      activeCallout = calloutBlock;
      lastStructuredBlock = null;
      return calloutBlock;
    };

    lines.forEach((line) => {
      const trimmedLine = line.trim();
      const lowerLine = trimmedLine.toLowerCase();

      if (lowerLine.startsWith('dear ')) {
        blocks.push({ kind: 'greeting', content: line });
        activeCallout = null;
        lastStructuredBlock = null;
        return;
      }

      if (lowerLine.startsWith('date & time') || lowerLine.startsWith('date and time')) {
        const detailValue = line.split(':').slice(1).join(':').trim();
        const detailBlock = { kind: 'detail', label: 'Date & Time', items: detailValue ? [detailValue] : [] };
        blocks.push(detailBlock);
        activeCallout = null;
        lastStructuredBlock = detailBlock;
        return;
      }

      if (lowerLine.startsWith('location:')) {
        const locationValue = line.split(':').slice(1).join(':').trim();
        const locationBlock = { kind: 'location', items: locationValue ? [locationValue] : [] };
        blocks.push(locationBlock);
        activeCallout = null;
        lastStructuredBlock = locationBlock;
        return;
      }

      if (lowerLine.startsWith('appointment details') || lowerLine.startsWith('appointment reminder')) {
        const title = (line.split(':')[0] || 'Appointment').trim();
        commitCallout('appointment', title || 'Appointment');
        return;
      }

      if (lowerLine.startsWith('special offer')) {
        const title = (line.split(':')[0] || 'Special Offer').trim();
        commitCallout('offer', title || 'Special Offer');
        return;
      }

      if (lowerLine.includes('policy:') || lowerLine.startsWith('policy')) {
        const title = (line.split(':')[0] || 'Important Policy').trim();
        commitCallout('policy', title || 'Important Policy');
        return;
      }

      if (lowerLine.startsWith('please be aware') || lowerLine.startsWith('please note')) {
        const existingPolicy = activeCallout?.calloutType === 'policy'
          ? activeCallout
          : commitCallout('policy', activeCallout?.title || 'Important Policy');
        existingPolicy.items.push(line);
        return;
      }

      if (lowerLine.startsWith('please be sure') || lowerLine.startsWith('please remember') || lowerLine.startsWith('remember to')) {
        const reminderBlock = activeCallout?.calloutType === 'reminder'
          ? activeCallout
          : commitCallout('reminder', 'Reminder');
        reminderBlock.items.push(line);
        return;
      }

      if (lowerLine.includes('scheduled for')) {
        const appointmentBlock = activeCallout?.calloutType === 'appointment'
          ? activeCallout
          : commitCallout('appointment', 'Appointment Details');
        appointmentBlock.items.push(line);
        return;
      }

      if (activeCallout && ['offer', 'policy', 'appointment', 'reminder'].includes(activeCallout.calloutType)) {
        activeCallout.items.push(line);
        return;
      }

      if (lastStructuredBlock && ['detail', 'location'].includes(lastStructuredBlock.kind)) {
        lastStructuredBlock.items.push(line);
        return;
      }

      blocks.push({ kind: 'text', content: line });
      activeCallout = null;
      lastStructuredBlock = null;
    });

    const calloutStyles = {
      appointment: {
        icon: <ScheduleIcon fontSize="small" />,
        color: theme.palette.info.main,
        bg: alpha(theme.palette.info.main, 0.06)
      },
      offer: {
        icon: <OfferIcon fontSize="small" />,
        color: theme.palette.success.main,
        bg: alpha(theme.palette.success.main, 0.06)
      },
      policy: {
        icon: <WarningIcon fontSize="small" />,
        color: theme.palette.warning.main,
        bg: alpha(theme.palette.warning.main, 0.06)
      },
      reminder: {
        icon: <InfoIcon fontSize="small" />,
        color: theme.palette.primary.main,
        bg: alpha(theme.palette.primary.main, 0.05)
      }
    };

    return (
      <Stack spacing={2} sx={{ mt: 1 }}>
        {blocks.map((block, index) => {
          if (block.kind === 'greeting') {
            return (
              <Typography key={`greeting-${index}`} variant="body1" sx={{ fontWeight: 600 }}>
                {block.content}
              </Typography>
            );
          }

          if (block.kind === 'detail') {
            return (
              <Box key={`detail-${index}`}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {block.label || 'Details'}
                </Typography>
                {block.items?.length > 0 && (
                  <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                    {block.items.map((item, itemIndex) => (
                      <Typography key={itemIndex} variant="body2" color="text.secondary">
                        {item}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Box>
            );
          }

          if (block.kind === 'location') {
            return (
              <Box key={`location-${index}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <LocationIcon sx={{ fontSize: 18, mt: 0.4, color: 'text.secondary' }} />
                {block.items?.length ? (
                  <Stack spacing={0.25}>
                    {block.items.map((item, itemIndex) => (
                      <Typography key={itemIndex} variant="body2" color="text.secondary">
                        {item}
                      </Typography>
                    ))}
                  </Stack>
                ) : null}
              </Box>
            );
          }

          if (block.kind === 'callout') {
            const config = calloutStyles[block.calloutType] || {
              icon: <InfoIcon fontSize="small" />,
              color: theme.palette.primary.main,
              bg: alpha(theme.palette.primary.main, 0.06)
            };

            return (
              <Box
                key={`callout-${index}`}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 2,
                  borderRadius: 2,
                  borderLeft: `3px solid ${config.color}`,
                  bgcolor: config.bg
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(config.color, 0.14),
                    color: config.color,
                    mt: 0.2
                  }}
                >
                  {config.icon}
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: config.color, letterSpacing: 0.4 }}>
                    {block.title || 'Details'}
                  </Typography>
                  <Stack spacing={0.75} sx={{ mt: block.items.length ? 0.75 : 0 }}>
                    {block.items.map((item, itemIndex) => (
                      <Typography key={itemIndex} variant="body2" sx={{ color: 'text.primary' }}>
                        {item}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              </Box>
            );
          }

          return (
            <Typography key={`text-${index}`} variant="body2" sx={{ color: 'text.primary' }}>
              {block.content}
            </Typography>
          );
        })}
      </Stack>
    );
  };

  return (
    <Paper 
      elevation={1} 
      sx={{ 
        p: 3, 
        mb: 2,
        bgcolor: activity.status === 'overdue' ? 'error.50' : 'background.paper',
        borderLeft: activity.status === 'overdue' ? '4px solid' : 'none',
        borderColor: 'error.main',
        '&:hover': {
          boxShadow: 2
        }
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {activity.title || `Activity #${activity.pabau_id || activity.id}`}
            </Typography>
            <Chip 
              label={activityType} 
              size="small" 
              sx={{ height: 22 }}
              variant="outlined" 
            />
            {activity.priority && activity.priority !== 'normal' && (
              <Chip 
                label={priority.label} 
                size="small" 
                color={priority.color}
                sx={{ height: 22 }}
              />
            )}
          </Box>

          {/* Metadata Row */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {activity.client_name && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {activity.client_name}
                </Typography>
              </Box>
            )}
            
            <Typography variant="caption" color="text.secondary">
              Created: {formatDate(activity.created_date)}
            </Typography>
            
            {activity.due_date && (
              <Typography 
                variant="caption" 
                color={new Date(activity.due_date) < new Date() ? 'error' : 'text.secondary'}
                fontWeight={new Date(activity.due_date) < new Date() ? 600 : 400}
              >
                Due: {formatDate(activity.due_date)}
              </Typography>
            )}
            
            {activity.assigned_to_name && (
              <Typography variant="caption" color="text.secondary">
                Assigned to: {activity.assigned_to_name}
              </Typography>
            )}
          </Box>
        </Box>

        <IconButton 
          onClick={() => setExpanded(!expanded)}
          size="small"
          sx={{ ml: 2, mr: { xs: 0, sm: 14 } }}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Content Preview - Show formatted content even when collapsed */}
      {!expanded && activity.notes && (
        <Box sx={{ mt: 2 }}>
          <Typography 
            variant="body2" 
            color="text.primary"
            sx={{ 
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {parsedContent.body}
          </Typography>
        </Box>
      )}

      {/* Expanded Content */}
      <Collapse in={expanded}>
        <Divider sx={{ my: 2, borderColor: 'divider' }} />
        {renderFormattedContent()}
        
        {/* Links - render more subtly */}
        {parsedContent.links && parsedContent.links.length > 0 && (
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <LinkIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Links
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {parsedContent.links.map((link, index) => (
                <Link 
                  key={index}
                  href={link.href} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{ fontSize: '0.875rem' }}
                >
                  {link.text}
                </Link>
              ))}
            </Box>
          </Box>
        )}

        {/* Images */}
        {parsedContent.images && parsedContent.images.length > 0 && (
          <Box sx={{ mt: 3 }}>
            {parsedContent.images.map((img, index) => (
              <Box key={index} sx={{ mb: 2, textAlign: 'center' }}>
                <img 
                  src={img.src} 
                  alt={img.alt}
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: 8,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

export default ActivityDisplay;
